import { useState, useEffect, useRef, useContext } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardMedia,
} from '@mui/material';
import { Icon } from '@iconify/react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import AuthContext from 'src/contexts/AuthContext';
import { styled } from '@mui/material/styles';

const ImageBox = styled(Card)(({ theme }) => ({
  position: 'relative',
  width: '150px',
  height: '150px',
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const ImageMedia = styled(CardMedia)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export default function MatchingChat() {
  const { matchingId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, customerId } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [matchingDetails, setMatchingDetails] = useState(null);
  const [foundAnnonce, setFoundAnnonce] = useState(null);
  const [lostAnnonce, setLostAnnonce] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const initializeConversation = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        
        if (!token || !isAuthenticated || !customerId) {
          navigate('/login');
          return;
        }

        // Récupérer les détails du matching
        const matchingResponse = await axios.get(
          `${process.env.REACT_APP_PUBLIC_API}/matches/${matchingId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        const matching = matchingResponse.data;
        setMatchingDetails(matching);
        
        // Extraire les IDs des annonces
        const getAnnonceId = (annonce) => {
          if (typeof annonce === 'string') {
            const match = annonce.match(/\/api\/annonces\/(\d+)/);
            return match ? match[1] : null;
          }
          return annonce.id;
        };

        const foundId = getAnnonceId(matching.annonceTrouve);
        const lostId = getAnnonceId(matching.annoncePerdu);

        // Récupérer les détails des annonces
        const [foundResponse, lostResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_PUBLIC_API}/annonces/${foundId}?professionalId=${customerId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          axios.get(`${process.env.REACT_APP_PUBLIC_API}/annonces/${lostId}?professionalId=${customerId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        // Récupérer les détails des attributs pour chaque annonce
        const fetchAttributeDetails = async (attributeUrl) => {
          try {
            const response = await axios.get(`${process.env.REACT_APP_PUBLIC_API}${attributeUrl.replace('/api', '')}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            return response.data;
          } catch (error) {
            console.error('Erreur lors de la récupération des attributs:', error);
            return null;
          }
        };

        // Récupérer les attributs pour l'annonce trouvée
        const foundAttributes = await Promise.all(
          (foundResponse.data.annonceAttributes || []).map(url => fetchAttributeDetails(url))
        );
        foundResponse.data.annonceAttributes = foundAttributes.filter(attr => attr !== null);

        // Récupérer les attributs pour l'annonce perdue
        const lostAttributes = await Promise.all(
          (lostResponse.data.annonceAttributes || []).map(url => fetchAttributeDetails(url))
        );
        lostResponse.data.annonceAttributes = lostAttributes.filter(attr => attr !== null);

        setFoundAnnonce(foundResponse.data);
        setLostAnnonce(lostResponse.data);

        const conversationData = {
          annonce: `/api/annonces/${foundId}`,
          matching: `/api/matches/${matchingId}`,
          customerAuthor: `/api/customers/${customerId}`,
          messages: [{
            content: "Début de la conversation",
            isPicture: false
          }]
        };

    

        // Créer ou récupérer la conversation
        try {
          const conversationResponse = await axios.post(
            `${process.env.REACT_APP_PUBLIC_API}/conversations`,
            conversationData,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
            }
          );

          setConversation(conversationResponse.data);
          
          // Charger les messages de la conversation
          const messagesResponse = await axios.get(
            `${process.env.REACT_APP_PUBLIC_API}/conversations/${conversationResponse.data.id}/messages`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          setMessages(messagesResponse.data);
        } catch (error) {
          console.error('Détails de l\'erreur:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          });
          throw error;
        }
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de la conversation:', error);
        setError('Impossible de charger la conversation');
      } finally {
        setLoading(false);
      }
    };

    if (matchingId) {
      initializeConversation();
    }
  }, [matchingId, isAuthenticated, navigate, customerId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversation) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${process.env.REACT_APP_PUBLIC_API}/messages`,
        {
          conversation: `/api/conversations/${conversation.id}`,
          content: newMessage,
          isPicture: false,
          customerAuthor: `/api/customers/${customerId}`
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        }
      );

      setMessages((prev) => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setError('Impossible d\'envoyer le message');
    }
  };

  const getImageUrl = (annonce) => {
    if (!annonce) return '';
    const baseUrl = process.env.REACT_APP_PUBLIC_URL;
    if (!annonce.filePath) {
      return `${baseUrl}/images/annonces/default/${annonce.category?.defaultImage}`;
    }
    return `${baseUrl}/images/annonces/${annonce.filePath}`;
  };

  if (!isAuthenticated) {
    return (
      <Box p={3}>
        <Alert severity="error">Vous devez être connecté pour accéder à cette fonctionnalité</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Card sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* En-tête de la conversation */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>Discussion sur un matching possible</Typography>
        
        {matchingDetails && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Objet trouvé */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                  <Typography variant="subtitle1" color="success.dark" gutterBottom>
                    Objet trouvé
                  </Typography>
                  {foundAnnonce ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <ImageBox>
                            <ImageMedia
                              image={getImageUrl(foundAnnonce)}
                              alt={foundAnnonce.title}
                              onClick={() => window.open(getImageUrl(foundAnnonce), '_blank')}
                              sx={{ cursor: 'pointer' }}
                            />
                          </ImageBox>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {foundAnnonce.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Catégorie: {foundAnnonce.category?.name}
                          </Typography>
                          
                          {/* Affichage des attributs */}
                          {foundAnnonce.annonceAttributes && foundAnnonce.annonceAttributes.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Caractéristiques
                              </Typography>
                              <Grid container spacing={1}>
                                {foundAnnonce.annonceAttributes.map((attr, index) => (
                                  <Grid item xs={12} sm={6} key={index}>
                                    <Box sx={{ 
                                      p: 1, 
                                      bgcolor: 'background.neutral',
                                      borderRadius: 1,
                                      mb: 1
                                    }}>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {attr.attribute}
                                      </Typography>
                                      <Typography variant="body2">
                                        {attr.attributeValue}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2">Chargement...</Typography>
                  )}
                </Paper>
              </Grid>

              {/* Objet perdu */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'warning.light' }}>
                  <Typography variant="subtitle1" color="warning.dark" gutterBottom>
                    Objet perdu
                  </Typography>
                  {lostAnnonce ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <ImageBox>
                            <ImageMedia
                              image={getImageUrl(lostAnnonce)}
                              alt={lostAnnonce.title}
                              onClick={() => window.open(getImageUrl(lostAnnonce), '_blank')}
                              sx={{ cursor: 'pointer' }}
                            />
                          </ImageBox>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {lostAnnonce.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Catégorie: {lostAnnonce.category?.name}
                          </Typography>
                          
                          {/* Affichage des attributs */}
                          {lostAnnonce.annonceAttributes && lostAnnonce.annonceAttributes.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Caractéristiques
                              </Typography>
                              <Grid container spacing={1}>
                                {lostAnnonce.annonceAttributes.map((attr, index) => (
                                  <Grid item xs={12} sm={6} key={index}>
                                    <Box sx={{ 
                                      p: 1, 
                                      bgcolor: 'background.neutral',
                                      borderRadius: 1,
                                      mb: 1
                                    }}>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {attr.attribute}
                                      </Typography>
                                      <Typography variant="body2">
                                        {attr.attributeValue}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}

                          {/* Informations utilisateur */}
                          {lostAnnonce.user && (
                            <Box sx={{ mt: 2 }}>
                              <Accordion>
                                <AccordionSummary
                                  expandIcon={<Icon icon="mdi:chevron-down" />}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'action.hover',
                                    },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar 
                                      sx={{ 
                                        width: 32, 
                                        height: 32,
                                        bgcolor: 'primary.main',
                                        fontSize: '0.875rem'
                                      }}
                                    >
                                      {lostAnnonce.user.firstname?.[0]}{lostAnnonce.user.lastname?.[0]}
                                    </Avatar>
                                    <Typography variant="subtitle2">
                                      {lostAnnonce.user.firstname} {lostAnnonce.user.lastname}
                                    </Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box sx={{ pl: 1 }}>
                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                      <strong>Email:</strong> {lostAnnonce.user.email}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                      <strong>Téléphone:</strong> {lostAnnonce.user.phone}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                      <strong>Adresse:</strong> {lostAnnonce.user.street}
                                    </Typography>
                                    {lostAnnonce.user.apptEtage && (
                                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                        <strong>Appartement:</strong> {lostAnnonce.user.apptEtage}
                                      </Typography>
                                    )}
                                    {lostAnnonce.user.batiment && (
                                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                        <strong>Bâtiment:</strong> {lostAnnonce.user.batiment}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                      <strong>Ville:</strong> {lostAnnonce.user.ville}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                      <strong>Code postal:</strong> {lostAnnonce.user.cp}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                      <strong>Pays:</strong> {lostAnnonce.user.country}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ mt: 2, pl: 1 }}>
                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                      <strong>Lieu de perte:</strong> {lostAnnonce.placeDescription}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                      <strong>Date:</strong> {new Date(lostAnnonce.date).toLocaleDateString()}
                                    </Typography>
                                    {lostAnnonce.details && (
                                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                        <strong>Détails:</strong> {lostAnnonce.details}
                                      </Typography>
                                    )}
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2">Chargement...</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Zone des messages */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.isFromCurrentUser ? 'flex-end' : 'flex-start',
            }}
          >
            <Paper
              sx={{
                p: 2,
                maxWidth: '70%',
                backgroundColor: message.isFromCurrentUser ? 'primary.main' : 'grey.100',
                color: message.isFromCurrentUser ? 'white' : 'text.primary',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1">{message.content}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {new Date(message.createdAt).toLocaleTimeString()}
              </Typography>
            </Paper>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Zone de saisie */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Écrivez votre message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            multiline
            maxRows={4}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
          >
            <Icon icon="mdi:send" width={24} />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );
} 